<template>
  <nuttrema-layout-form-standard
     theme="measurements-style"
    :edit="true"
    :saveError="PersonMeasurement.saveError">

    <template slot="center">
      <el-form
         label-width="12em"
         label-position="right"
        :ref="formRef"
        :rules="rules"
        :model="formData">

        <section>
          <h4 class="section-title">
            {{ $t('label.participant') }}
          </h4>
          <div class="section-items is-info-card">
            <user-info-small-card
               class="is-in-section"
              :reactive="false"
              :personDetails="PersonDetails.data">
            </user-info-small-card>
          </div>
        </section>

        <section>
          <h4 class="section-title">{{ $t('label.measurement_time') }}</h4>
          <div class="section-items">
            <el-form-item
               prop="measurement_date"
              :label="$t('label.date')">
              <el-col :span="20">
                <el-date-picker
                   v-model="formData.measurement_date"
                   type="date"
                   format="dd/MM/yyyy"
                   value-format="yyyy-MM-dd"
                   align="right"
                   class="w-100"
                  :disabled="!edit"
                  :clearable="false"
                  :picker-options="{ firstDayOfWeek: 1, disabledDate: checkDateDisabled }">
                </el-date-picker>
              </el-col>
            </el-form-item>
            <el-form-item
               prop="measurement_time"
              :label="$t('label.time')">
              <el-col :span="16">
                <el-time-picker
                   v-model="formData.measurement_time"
                   class="w-100"
                   type="fixed-time"
                   format="HH:mm"
                   value-format="HH:mm"
                  :editable="true"
                  :disabled="!edit"
                  :picker-options="timePicker">
                </el-time-picker>
              </el-col>
            </el-form-item>
          </div>
        </section>

        <section
           v-for="section in _get(PersonMeasurement, 'data.MeasurementTypeGroups', [])"
           v-if="section.has_overridable"
          :key="section.id">
          <h4 class="section-title">{{ section.name }}</h4>
          <div class="section-items">
            <template v-for="trackingGroup in section.TrackingGroups">
              <component
                 v-bind:is="componentName(trackingGroup.display)"
                :group="trackingGroup"
                :edit="edit"
                :showAllMeasurements="options.showAllMeasurements"
                :prefferedInputType="options.prefferedInputType"
                :allData="allData"
                :originalData="originalData"
                @set:value="setValueEvent">
              </component>
            </template>
          </div>
        </section>

        <section>
          <div class="section-help">
            <p>{{ $t('label.measurements_note') }}</p>
          </div>
        </section>

      </el-form>
    </template>
    <!-- end ~ center -->

    <template slot="footer" v-if="measurementID !== -1">
      <nav class="footer-row">
        <div class="footer-row__column-left">
          <el-button type="primary" size="small" :loading="isSaving" @click="submitForm">{{ $t('label.save') }}</el-button>

          <el-button type="text" size="small" @click="cancelChanges">
            <span v-if="showDoneButton">{{ $t('label.done') }}</span>
            <span v-else>{{ $t('label.cancel') }}</span>
          </el-button>
        </div>
        <div class="footer-row__column-right">
          <popover-question-alert
             buttonTitle="label.delete"
             buttonType="danger"
             popoverWidth="280"
             popoverTitle="label.delete"
             popoverText="label.delete_sure"
             popoverPlacement="top-end"
             buttonSize="small"
            :popoverShouldShow="true"
            @confirmed="removeMeasurement">
          </popover-question-alert>
        </div>
      </nav>
    </template>

    <template slot="footer" v-else>
      <nav class="footer-row">
        <div class="footer-row__column-left">
          <el-button type="primary" size="small" :loading="isSaving" @click="submitForm">{{ $t('label.save') }}</el-button>
        </div>
        <div class="footer-row__column-right">
          <el-button type="text" size="small" @click="cancelChanges">
            <span v-if="showDoneButton">{{ $t('label.done') }}</span>
            <span v-else>{{ $t('label.cancel') }}</span>
          </el-button>
        </div>
      </nav>
    </template>

  </nuttrema-layout-form-standard>
</template>

<script>
/* eslint-disable object-shorthand */
import Vue from 'vue';
import moment from 'moment';
import { each, isEmpty, has, cloneDeep, isNil, isNull, omit, get } from 'lodash';

import NuttremaLayoutFormStandard from 'components/ui/layout/nuttrema-layout-form-standard';
import FormStandardBehavior from 'src/components/mixins/form-standard-behavior';

import UserInfoSmallCard from 'components/ui/card/user-info-small-card';
import PopoverQuestionAlert from 'components/ui/alert/nuttrema-popover-question-alert';

import GroupSingle from './tracked-measurements-form-input-groups/group-single';
import GroupMultiple from './tracked-measurements-form-input-groups/group-multiple';
import GroupPercentageValue from './tracked-measurements-form-input-groups/group-percentage-value';
import GroupPercentagePrimaryValue from './tracked-measurements-form-input-groups/group-percentage-primary-value';
import GroupValueLevel from './tracked-measurements-form-input-groups/group-value-level';

export default {
  name: 'form-evolution-cred-measurement',
  mixins: [FormStandardBehavior],
  props: {
    measurementID: {
      required: true,
      default: null,
    },
    PersonDetails: {
      required: true,
      default: null,
    },
    PersonMeasurement: {
      type: Object,
      required: true,
      default: null,
    },
    edit: {
      type: Boolean,
      required: false,
      default: false,
    },
    options: {
      type: Object,
      required: true,
      default: null,
    },
    period: {
      type: String,
      required: false,
      default: null,
    },
    dateFrom: {
      type: String,
      required: false,
      default: null,
    },
    dateTo: {
      type: String,
      required: false,
      default: null,
    },
  },
  components: {
    UserInfoSmallCard,
    GroupSingle,
    GroupMultiple,
    GroupValueLevel,
    GroupPercentageValue,
    PopoverQuestionAlert,
    NuttremaLayoutFormStandard,
    GroupPercentagePrimaryValue,
  },
  data() {
    return {
      rules: {},
      allData: {},
      formData: {
        measurement_time: null,
        measurement_date: null,
        measurements: {},
      },
      calculatedFrom: {},
      calculatedManuallyValues: {},
      timePicker: {},
    };
  },
  // computed: {
  //   timePicker() {
  //     const today = new Date();
  //     const todayMeasurement = moment(today).isSame(moment(this.formData.measurement_date), 'day');

  //     return todayMeasurement ? { selectableRange: [`00:00:00 - ${this.formData.measurement_time}:00`] } : null;
  //   },
  // },
  methods: {
    componentName(value) {
      const name = value.replace(/_/g, '-');
      return `${name}`;
    },
    setValueEvent(params) {
      console.log('setValueEvent', params);
      Vue.set(this.allData, params.code, params.data);
      if (params.overridable) Vue.set(this.formData.measurements, params.code, params.data);
    },
    removeMeasurement() {
      this.$emit('removeMeasurement', this.measurementID);
    },
    checkDateDisabled(date) {
      return date.getTime() >= new Date();
    },
    checkAlternative(code, fields) {
      let checked = 0;
      each(fields, (field) => {
        if (isNull(this.formData[field]) || this.formData[field] === 0) checked += 1;
      });
      return checked;
    },
    checkCanBeCalculated(code) {
      let calculable = false;
      if (has(this.calculatedFrom, code)) {
        each(this.calculatedFrom[code], (fields) => {
          if (this.checkAlternative(code, fields) === 0) calculable = true;
        });
      } else {
        calculable = true;
      }

      return calculable ? {
        label: 'calculable',
        class: 'el-input-calculable',
      } : {
        label: 'incalculable',
        class: 'el-input-incalculable',
      };
    },
    handleManuallySetChangedEvent(measurementTypeCode) {
      console.log('handleManuallySetChangedEvent', measurementTypeCode, this.calculatedManuallyValues[measurementTypeCode]);
      if (!this.calculatedManuallyValues[measurementTypeCode]) {
        Vue.set(this.formData, measurementTypeCode, this.originalData[measurementTypeCode]);
      }
    },
    prepareRule(validation, calculated) {
      const rule = {
        type: 'object',
        fields: {
          measurement_type_id: { type: 'number', required: true },
        },
      };

      if (!isEmpty(validation)) {
        const value = {};

        if (has(validation, 'type') && validation.type === 'number') {
          Vue.set(value, 'type', 'number');
        }

        if (has(validation, 'min')) {
          Vue.set(value, 'min', validation.min);
        }

        if (has(validation, 'max')) {
          Vue.set(value, 'max', validation.max);
        }

        if (has(validation, 'allow_empty') && !validation.allow_empty) {
          Vue.set(value, 'required', true);
        }

        if (calculated) {
          Vue.set(value, 'min', 0);
          Vue.set(value, 'required', false);
        }
        Vue.set(rule.fields, 'value', value);
      }
      return rule;
    },
    setRule(measurementType) {
      const rule = this.prepareRule(measurementType.validation, measurementType.calculated);
      Vue.set(this.rules, measurementType.code, rule);
    },
    setFormData() {
      const measurementTimestamp = get(this.PersonMeasurement, 'data.measurement_timestamp', new Date()) || new Date();

      const currentMesurementDate = moment(measurementTimestamp).toDate();
      const currentTime = moment(measurementTimestamp).subtract(1, 'minute').format('HH:mm');

      Vue.set(this.formData, 'measurement_date', currentMesurementDate);
      Vue.set(this.formData, 'measurement_time', currentTime);

      const measurement = get(this.PersonMeasurement, 'data.MeasurementTypeGroups', []);
      if (!isEmpty(measurement)) {
        each(measurement.MeasurementTypeGroups, (measurementTypeGroup) => {
          each(measurementTypeGroup.TrackingGroups, (trackingGroup) => {
            each(trackingGroup.MeasurementTypes, (measurementType) => {
              this.setRule(measurementType);
            });
          });
        });
      }
    },
    getFormData() {
      const payload = {
        measurement_date: this.formData.measurement_date,
        measurement_time: this.formData.measurement_time,
        measurements: {},
      };
      each(this.formData.measurements, (measurement, key) => {
        let shouldOmitAndSet = false;
        const percentageValue = get(measurement, 'percentage_value');
        const setManually = get(measurement, 'set_manually');

        if (percentageValue && this.options.prefferedInputType !== 'both') {
          if (this.options.prefferedInputType === percentageValue) {
            shouldOmitAndSet = true;
          }
        } else {
          shouldOmitAndSet = true;
        }

        if (!isNil(setManually) && setManually === false) {
          shouldOmitAndSet = false;
        }

        if (shouldOmitAndSet) {
          const omited = omit(measurement, ['percentage_value', 'set_manually']);
          Vue.set(payload.measurements, key, omited);
        }
      });
      return payload;
    },
  },
  mounted() {
    this.stateData = this.PersonMeasurement;
    this.originalData = cloneDeep(this.allData);
    this.formRef = 'form-evolution-cred-measurement';
    this.setFormData();
  },
  watch: {
    'stateData.saved': {
      handler: function () {},
      deep: true,
    },
  },
};

</script>

<style lang="scss">
  .form-standard-layout--participant-evolution-settings {
    .form-standard-layout__content {
      .is-not-overridable { display: none; }
      &.show-all-included-measurements {
        .is-not-overridable { display: block; }
      }
    }
    .el-button.is-disabled, .el-button.is-disabled:focus, .el-button.is-disabled:hover {
      background-color: #f7f7f7;
      border-color: #f7f7f7;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/professional/persons/participants/participant-evolution/measurement/form-evolution-cred-measurement.vue